import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Eye,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  DollarSign,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { saveToLocalDB, getFromLocalDB, addToSyncQueue } from '@/lib/offlineStorage';

const API = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const NASOrders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    supplierName: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedArrival: '',
    items: [],
    totalAmount: 0,
    totalCost: 0,
    expectedRevenue: 0,
    expectedProfit: 0,
    paymentMethod: '',
    expenses: 0,
    otherExpenses: [],
    notes: ''
  });
  
  const [selectedProduct, setSelectedProduct] = useState('');
  const [newItem, setNewItem] = useState({ 
    productId: '',
    name: '', 
    quantity: 0, 
    unit: 'sack', 
    purchasePrice: 0,
    sellingPrice: 0
  });
  
  const [newExpense, setNewExpense] = useState({ description: '', amount: 0 });
  
  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);
  
  const loadOrders = async () => {
    setIsLoading(true);
    try {
      if (navigator.onLine) {
        const response = await axios.get(`${API}/api/nas/orders`);
        setOrders(response.data);
        await saveToLocalDB('nas_orders', response.data);
      } else {
        const localOrders = await getFromLocalDB('nas_orders');
        if (localOrders && localOrders.length > 0) {
          setOrders(localOrders);
          toast.info('Offline mode - showing cached orders');
        }
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      const localOrders = await getFromLocalDB('nas_orders');
      if (localOrders) {
        setOrders(localOrders);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadProducts = async () => {
    try {
      if (navigator.onLine) {
        const response = await axios.get(`${API}/api/nas/products`);
        setProducts(response.data);
        await saveToLocalDB('nas_products', response.data);
      } else {
        const localProducts = await getFromLocalDB('nas_products');
        if (localProducts) {
          setProducts(localProducts);
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
      const localProducts = await getFromLocalDB('nas_products');
      if (localProducts) {
        setProducts(localProducts);
      }
    }
  };
  
  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(productId);
      setNewItem({
        productId: product.id,
        name: product.name,
        quantity: 0,
        unit: 'sack',
        purchasePrice: product.purchasePricePerSack || product.wholesalePrice || 0,
        sellingPrice: product.sellingPricePerSack || product.wholesalePrice || 0
      });
    }
  };
  
  const handleUnitChange = (unit) => {
    const product = products.find(p => p.id === newItem.productId);
    if (product) {
      if (unit === 'kg') {
        setNewItem({
          ...newItem,
          unit,
          purchasePrice: product.purchasePricePerKg || 0,
          sellingPrice: product.sellingPricePerKg || product.retailPrice || 0
        });
      } else {
        setNewItem({
          ...newItem,
          unit,
          purchasePrice: product.purchasePricePerSack || product.wholesalePrice || 0,
          sellingPrice: product.sellingPricePerSack || product.wholesalePrice || 0
        });
      }
    } else {
      setNewItem({ ...newItem, unit });
    }
  };
  
  const calculateTotals = (items, expenses, otherExpenses) => {
    const totalCost = items.reduce((sum, item) => sum + (item.quantity * item.purchasePrice), 0);
    const expectedRevenue = items.reduce((sum, item) => sum + (item.quantity * item.sellingPrice), 0);
    const totalExpenses = expenses + (otherExpenses || []).reduce((sum, e) => sum + e.amount, 0);
    const expectedProfit = expectedRevenue - totalCost - totalExpenses;
    
    return {
      totalAmount: totalCost + totalExpenses,
      totalCost,
      expectedRevenue,
      expectedProfit,
      totalExpenses
    };
  };
  
  const handleAddItem = () => {
    if (!newItem.name || newItem.quantity <= 0) {
      toast.error('Please select a product and enter quantity');
      return;
    }
    
    const itemCost = newItem.quantity * newItem.purchasePrice;
    const itemRevenue = newItem.quantity * newItem.sellingPrice;
    const itemProfit = itemRevenue - itemCost;
    
    const updatedItems = [...formData.items, { 
      ...newItem, 
      cost: itemCost,
      revenue: itemRevenue,
      profit: itemProfit
    }];
    
    const totals = calculateTotals(updatedItems, formData.expenses, formData.otherExpenses);
    
    setFormData({ ...formData, items: updatedItems, ...totals });
    setNewItem({ productId: '', name: '', quantity: 0, unit: 'sack', purchasePrice: 0, sellingPrice: 0 });
    setSelectedProduct('');
  };
  
  const handleRemoveItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(updatedItems, formData.expenses, formData.otherExpenses);
    setFormData({ ...formData, items: updatedItems, ...totals });
  };
  
  const handleAddExpense = () => {
    if (!newExpense.description || newExpense.amount <= 0) {
      toast.error('Please enter expense details');
      return;
    }
    
    const updatedExpenses = [...(formData.otherExpenses || []), newExpense];
    const totals = calculateTotals(formData.items, formData.expenses, updatedExpenses);
    
    setFormData({ ...formData, otherExpenses: updatedExpenses, ...totals });
    setNewExpense({ description: '', amount: 0 });
  };
  
  const handleRemoveExpense = (index) => {
    const updatedExpenses = formData.otherExpenses.filter((_, i) => i !== index);
    const totals = calculateTotals(formData.items, formData.expenses, updatedExpenses);
    setFormData({ ...formData, otherExpenses: updatedExpenses, ...totals });
  };
  
  const handleExpensesChange = (value) => {
    const expenses = parseFloat(value) || 0;
    const totals = calculateTotals(formData.items, expenses, formData.otherExpenses);
    setFormData({ ...formData, expenses, ...totals });
  };
  
  const handleSaveOrder = async () => {
    if (!formData.supplierName || formData.items.length === 0) {
      toast.error('Please fill in required fields and add at least one item');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        const response = await axios.post(`${API}/api/nas/orders`, formData);
        const newOrder = response.data;
        const updatedOrders = [newOrder, ...orders];
        setOrders(updatedOrders);
        await saveToLocalDB('nas_orders', updatedOrders);
        toast.success('Order created successfully');
      } else {
        const orderData = {
          ...formData,
          id: `local_${Date.now()}`,
          status: 'pending',
          amountPaid: 0,
          createdAt: new Date().toISOString(),
          _pendingSync: true
        };
        const updatedOrders = [orderData, ...orders];
        setOrders(updatedOrders);
        await saveToLocalDB('nas_orders', updatedOrders);
        await addToSyncQueue({ type: 'CREATE_ORDER', data: orderData, timestamp: new Date().toISOString() });
        toast.success('Order created (will sync when online)');
      }
      resetForm();
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setIsSaving(false);
    }
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // If marking as delivered, confirm with user that stock will be added to bodega
      if (newStatus === 'delivered') {
        const order = orders.find(o => o.id === orderId);
        if (order && order.status !== 'delivered') {
          const itemCount = order.items?.length || 0;
          if (!window.confirm(`Mark order as Delivered?\n\nThis will automatically add ${itemCount} item(s) to Bodega stock.\n\nSupplier: ${order.supplierName}\nItems: ${order.items?.map(i => `${i.quantity} ${i.unit} ${i.name}`).join(', ')}`)) {
            return;
          }
        }
      }
      
      if (navigator.onLine) {
        const response = await axios.put(`${API}/api/nas/orders/${orderId}/status?status=${newStatus}`);
        
        // Show stock received message if items were added to bodega
        if (response.data.stockReceived && response.data.receivedItems) {
          const itemsList = response.data.receivedItems
            .map(item => `• ${item.quantity} ${item.unit} ${item.productName} (New stock: ${item.newBodegaStock})`)
            .join('\n');
          toast.success(
            `Order delivered! Stock added to Bodega:\n${response.data.totalItemsReceived} item(s) received`,
            { duration: 5000 }
          );
        } else {
          toast.success(`Order marked as ${ORDER_STATUS[newStatus].label}`);
        }
      } else {
        toast.success(`Order marked as ${ORDER_STATUS[newStatus].label} (will sync when online)`);
      }
      
      const updatedOrders = orders.map(o => 
        o.id === orderId ? { 
          ...o, 
          status: newStatus, 
          updatedAt: new Date().toISOString(),
          stockReceived: newStatus === 'delivered' ? true : o.stockReceived,
          deliveredAt: newStatus === 'delivered' ? new Date().toISOString() : o.deliveredAt
        } : o
      );
      setOrders(updatedOrders);
      await saveToLocalDB('nas_orders', updatedOrders);
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    }
  };
  
  const resetForm = () => {
    setFormData({
      supplierName: '',
      orderDate: new Date().toISOString().split('T')[0],
      expectedArrival: '',
      items: [],
      totalAmount: 0,
      totalCost: 0,
      expectedRevenue: 0,
      expectedProfit: 0,
      paymentMethod: '',
      expenses: 0,
      otherExpenses: [],
      notes: ''
    });
    setSelectedProduct('');
    setNewItem({ productId: '', name: '', quantity: 0, unit: 'sack', purchasePrice: 0, sellingPrice: 0 });
    setNewExpense({ description: '', amount: 0 });
    setIsAddModalOpen(false);
  };
  
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Orders</h1>
          <p className="text-slate-500">Track supplier orders and deliveries ({orders.length} orders)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadOrders} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
            <Plus size={18} className="mr-2" />
            New Order
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search by supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(ORDER_STATUS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>{value.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Orders List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
            <p className="text-slate-500 mt-2">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-10 text-center">
              <Truck className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500 mb-4">
                {orders.length === 0 ? 'No orders yet. Create your first order!' : 'No orders match your search.'}
              </p>
              {orders.length === 0 && (
                <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus size={18} className="mr-2" />
                  Create First Order
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map(order => (
            <Card key={order.id} className={`bg-white ${order._pendingSync ? 'border-amber-300' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">{order.supplierName}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${ORDER_STATUS[order.status]?.color}`}>
                        {ORDER_STATUS[order.status]?.label}
                      </span>
                      {order.stockReceived && (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                          <Package size={12} />
                          Stock Received
                        </span>
                      )}
                      {order._pendingSync && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">
                          Pending Sync
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Ordered: {order.orderDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        Expected: {order.expectedArrival || 'TBD'}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign size={14} />
                        Cost: ₱{(order.totalCost || order.totalAmount || 0).toLocaleString()}
                      </span>
                      {order.expectedProfit !== undefined && (
                        <span className={`flex items-center gap-1 font-medium ${order.expectedProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {order.expectedProfit >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          Profit: ₱{order.expectedProfit?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => setViewingOrder(order)}>
                      <Eye size={16} className="mr-1" /> View
                    </Button>
                    {order.status === 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.id, 'confirmed')}>
                        Confirm
                      </Button>
                    )}
                    {order.status === 'confirmed' && (
                      <Button size="sm" variant="outline" onClick={() => updateOrderStatus(order.id, 'shipped')}>
                        <Truck size={16} className="mr-1" /> Shipped
                      </Button>
                    )}
                    {/* Delivered button - always visible for non-delivered/cancelled orders */}
                    {order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700" 
                        onClick={() => updateOrderStatus(order.id, 'delivered')}
                      >
                        <CheckCircle size={16} className="mr-1" /> Delivered
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Add Order Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Supplier Order</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Supplier Name *</Label>
                <Input
                  value={formData.supplierName}
                  onChange={(e) => setFormData({...formData, supplierName: e.target.value})}
                  placeholder="e.g., ABC Feeds Corp"
                />
              </div>
              
              <div>
                <Label>Order Date</Label>
                <Input
                  type="date"
                  value={formData.orderDate}
                  onChange={(e) => setFormData({...formData, orderDate: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Expected Arrival</Label>
                <Input
                  type="date"
                  value={formData.expectedArrival}
                  onChange={(e) => setFormData({...formData, expectedArrival: e.target.value})}
                />
              </div>
            </div>
            
            {/* Order Items - Product Selection */}
            <div>
              <Label className="mb-2 block">Order Items</Label>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                {/* Product Selection Dropdown */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <Select value={selectedProduct} onValueChange={handleProductSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select product..." />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map(product => (
                          <SelectItem key={product.id} value={product.id}>
                            <div className="flex items-center gap-2">
                              <Package size={14} />
                              {product.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    className="col-span-2"
                    type="number"
                    placeholder="Qty"
                    value={newItem.quantity || ''}
                    onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 0})}
                  />
                  <Select value={newItem.unit} onValueChange={handleUnitChange}>
                    <SelectTrigger className="col-span-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sack">Sack</SelectItem>
                      <SelectItem value="kg">Kg</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="piece">Piece</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    className="col-span-2"
                    type="number"
                    placeholder="Buy ₱"
                    value={newItem.purchasePrice || ''}
                    onChange={(e) => setNewItem({...newItem, purchasePrice: parseFloat(e.target.value) || 0})}
                    title="Purchase Price"
                  />
                  <Button className="col-span-2" onClick={handleAddItem} disabled={!newItem.name}>
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </div>
                
                {/* Show selected product prices */}
                {selectedProduct && (
                  <div className="flex gap-4 text-sm bg-white p-2 rounded border">
                    <span className="text-slate-600">
                      <span className="font-medium">Purchase:</span> ₱{newItem.purchasePrice.toLocaleString()}/{newItem.unit}
                    </span>
                    <span className="text-green-600">
                      <span className="font-medium">Selling:</span> ₱{newItem.sellingPrice.toLocaleString()}/{newItem.unit}
                    </span>
                    {newItem.quantity > 0 && (
                      <span className={`font-medium ${(newItem.sellingPrice - newItem.purchasePrice) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        Profit/unit: ₱{(newItem.sellingPrice - newItem.purchasePrice).toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Items Table */}
                {formData.items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-2">Product</th>
                          <th className="text-center p-2">Qty</th>
                          <th className="text-center p-2">Unit</th>
                          <th className="text-right p-2">Purchase</th>
                          <th className="text-right p-2">Selling</th>
                          <th className="text-right p-2">Cost</th>
                          <th className="text-right p-2 text-green-700">Profit</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="p-2 font-medium">{item.name}</td>
                            <td className="text-center p-2">{item.quantity}</td>
                            <td className="text-center p-2">{item.unit}</td>
                            <td className="text-right p-2">₱{item.purchasePrice?.toLocaleString()}</td>
                            <td className="text-right p-2 text-green-600">₱{item.sellingPrice?.toLocaleString()}</td>
                            <td className="text-right p-2">₱{item.cost?.toLocaleString()}</td>
                            <td className={`text-right p-2 font-medium ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ₱{item.profit?.toLocaleString()}
                            </td>
                            <td className="p-2 text-center">
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index)}>
                                <XCircle size={16} className="text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            
            {/* Expenses Section */}
            <div>
              <Label className="mb-2 block">Expenses</Label>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Delivery/Shipping</Label>
                    <Input
                      type="number"
                      value={formData.expenses || ''}
                      onChange={(e) => handleExpensesChange(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Add Other Expense</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Description"
                        value={newExpense.description}
                        onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder="₱"
                        value={newExpense.amount || ''}
                        onChange={(e) => setNewExpense({...newExpense, amount: parseFloat(e.target.value) || 0})}
                        className="w-24"
                      />
                      <Button size="sm" onClick={handleAddExpense}>
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Other Expenses List */}
                {formData.otherExpenses && formData.otherExpenses.length > 0 && (
                  <div className="space-y-1">
                    {formData.otherExpenses.map((expense, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                        <span>{expense.description}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">₱{expense.amount.toLocaleString()}</span>
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveExpense(index)}>
                            <XCircle size={14} className="text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Profit Summary */}
            {formData.items.length > 0 && (
              <div className="bg-gradient-to-r from-slate-50 to-green-50 rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <TrendingUp size={18} />
                  Order Summary & Profit
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Total Cost</p>
                    <p className="text-lg font-semibold text-slate-800">₱{formData.totalCost?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Expenses</p>
                    <p className="text-lg font-semibold text-orange-600">
                      ₱{((formData.expenses || 0) + (formData.otherExpenses || []).reduce((s, e) => s + e.amount, 0)).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Expected Revenue</p>
                    <p className="text-lg font-semibold text-blue-600">₱{formData.expectedRevenue?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Expected Profit</p>
                    <p className={`text-lg font-bold ${formData.expectedProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formData.expectedProfit >= 0 ? '+' : ''}₱{formData.expectedProfit?.toLocaleString()}
                    </p>
                    {formData.totalCost > 0 && (
                      <p className={`text-xs ${formData.expectedProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        ({((formData.expectedProfit / formData.totalCost) * 100).toFixed(1)}% margin)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <Select value={formData.paymentMethod} onValueChange={(v) => setFormData({...formData, paymentMethod: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="gcash">GCash</SelectItem>
                    <SelectItem value="credit">Credit/Terms</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSaveOrder} disabled={isSaving} className="bg-green-600 hover:bg-green-700">
              {isSaving ? 'Creating...' : 'Create Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Order Modal */}
      <Dialog open={!!viewingOrder} onOpenChange={() => setViewingOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Supplier</p>
                <p className="font-semibold">{viewingOrder.supplierName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Order Date</p>
                  <p>{viewingOrder.orderDate}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Expected Arrival</p>
                  <p>{viewingOrder.expectedArrival || 'TBD'}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Items</p>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {viewingOrder.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span>{item.quantity} {item.unit}s - {item.name}</span>
                      <span className="font-medium">₱{(item.quantity * item.price).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Total</span>
                    <span>₱{(viewingOrder.totalAmount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              {viewingOrder.notes && (
                <div>
                  <p className="text-sm text-slate-500">Notes</p>
                  <p className="text-sm">{viewingOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASOrders;
